import React, { useState } from 'react';
import styled from 'styled-components';
import { Alert, Flex, Typography, Button, Grid } from 'antd';
import { CDN_BASE_URL, COMPANY_NAME } from '../../../constants';

import {
  CloseCircleTwoTone,
  ThunderboltTwoTone,
  SmileTwoTone,
  FileImageTwoTone,
  RocketOutlined,
} from '@ant-design/icons';
import { t } from '../../../utils/i18n';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import {
  paymentErrors,
  useInitiateShorePayOnlineAccountSetup,
} from '@shared/hooks/payments';
import { useAppSelector } from '@shared/hooks/use-app-selector';
import createQueryConfig from '../../../queries';

import { PaymentCaptureTaxIDModal } from './PaymentCaptureTaxIDModal';
import { useNavigate } from 'react-router-dom';
import { IMerchantPaymentSettingsV2, IPaymentMethodV2 } from '@shared/types';
import { useQuery } from '@tanstack/react-query';

const { useBreakpoint } = Grid;

const StyledBanner = styled(Alert)`
  max-width: 900px;
  margin-bottom: 30px;
  border: none;
  border-radius: 8px;
  background-color: white;
  padding: 0;
  box-shadow:
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);

  .ant-v5-alert-close-icon {
    font-size: 16px;
    margin-top: 20px;
    margin-right: 20px;
  }
`;

export function PaymentOnboardingBanner() {
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [visible, setVisible] = useState(true);
  const [isCaptureTaxIDModalOpen, setIsCaptureTaxIDModalOpen] = useState(false);

  const { queryClient, apiClient, trackMethod } = useGlobals()!;
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const { merchantSettings } = useMerchantSettings();
  const { merchantId, trialDaysLeft, featureToggles } = merchantSettings || {};

  const isTrialMerchant = (trialDaysLeft || 0) > 0;

  const isWhitelabel = currentMerchant.whiteLabel !== null;

  const {
    mutate: mutateInitiateShorePayOnlineAccountSetup,
    isPending: isLoadingInitiateShorePayOnlineAccountSetup,
  } = useInitiateShorePayOnlineAccountSetup();

  const {
    data: paymentSettingsV2,
    isLoading: isLoadingPaymentSettingsV2,
    isFetching: isFetchingPaymentSettings,
  } = useQuery<IMerchantPaymentSettingsV2>({
    ...queryConfig.merchantPaymentSettingsV2b({ merchantId }),
    enabled: Boolean(merchantId),
  });

  const paymentMethods: IPaymentMethodV2[] = (
    paymentSettingsV2?.payment_methods ?? []
  ).filter(({ name, status }) => {
    if (!(featureToggles?.paypal_payment_enabled ?? false) && name === 'paypal')
      return false;
    if (COMPANY_NAME !== 'shore' && name === 'shorepay_online') return false;
    if (name === 'stripe' && status === 'not_setup') return false;
    return true;
  });

  const isShorePayOnlineActive =
    paymentMethods.find((m) => m.name === 'shorepay_online')?.status ===
    'active';

  const handleOnBannerCloseClick = () => {
    trackMethod(
      'application-settings-app_online_payment_settings_screen_banner_close_clicked'
    );
    setVisible(false);
  };

  const handleOnShorePayOnlineAccountButtonClick = () => {
    trackMethod(
      'application-settings-app_online_payment_settings_screen_banner_start_setup_clicked'
    );
    mutateInitiateShorePayOnlineAccountSetup(
      {
        merchantId,
        company: merchantSettings?.company,
      },
      {
        onError: (error: unknown) => {
          if (error instanceof Error) {
            if (
              error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)
            ) {
              setIsCaptureTaxIDModalOpen(true);
            }
          }
        },
      }
    );
  };

  const handleOnUpgradeContractClick = () => {
    trackMethod('application-settings-app_choose_plan', {
      days_trial_left: trialDaysLeft,
    });
    navigate(`/subscription`);
  };

  const handleOnCaptureTaxIDModalClose = () => {
    setIsCaptureTaxIDModalOpen(false);
  };

  const handleOnCaptureTaxIDModalFormSubmit = ({
    taxId,
  }: {
    taxId: string;
  }) => {
    mutateInitiateShorePayOnlineAccountSetup(
      {
        taxId,
        merchantId,
        company: merchantSettings?.company,
      },
      {
        onSuccess: () => setIsCaptureTaxIDModalOpen(false),
      }
    );
  };

  if (
    !visible ||
    !paymentMethods ||
    isLoadingPaymentSettingsV2 ||
    isShorePayOnlineActive
  )
    return null;

  return (
    <>
      <StyledBanner
        showIcon={false}
        closable
        onClose={handleOnBannerCloseClick}
        description={
          <Flex justify="space-between">
            {screens.md && (
              <Flex
                style={{
                  width: 400,
                  backgroundColor: '#E6FFFB',
                  background: `url("${CDN_BASE_URL}/images/banner_feature_deposit_highlights.webp") no-repeat`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                justify="center"
              >
                <FileImageTwoTone
                  twoToneColor="#00D0BE"
                  style={{ fontSize: 48, display: 'none' }}
                />
              </Flex>
            )}

            <Flex
              vertical
              align="flex-start"
              style={{
                paddingTop: 24,
                paddingLeft: 24,
                paddingBottom: 24,
                flex: 'auto',
              }}
            >
              <Flex vertical>
                <Typography.Title level={3} style={{ marginTop: 0 }}>
                  {t('application-settings.payments.onboarding-banner.title')}
                </Typography.Title>
                <Typography.Paragraph>
                  {t(
                    'application-settings.payments.onboarding-banner.description'
                  )}
                </Typography.Paragraph>
              </Flex>
              <Flex vertical gap={5}>
                <Flex gap={8}>
                  <CloseCircleTwoTone
                    twoToneColor="#FF2E52"
                    style={{ fontSize: 16 }}
                  />
                  <Typography.Text style={{ fontSize: 16 }}>
                    {t(
                      'application-settings.payments.onboarding-banner.benefits.benefit-one.title'
                    )}
                  </Typography.Text>
                </Flex>
                <Typography.Paragraph>
                  {t(
                    'application-settings.payments.onboarding-banner.benefits.benefit-one.description'
                  )}
                </Typography.Paragraph>
              </Flex>
              <Flex vertical gap={5}>
                <Flex gap={8}>
                  <ThunderboltTwoTone
                    twoToneColor="#FADB14"
                    style={{ fontSize: 16 }}
                  />
                  <Typography.Text style={{ fontSize: 16 }}>
                    {t(
                      'application-settings.payments.onboarding-banner.benefits.benefit-two.title'
                    )}
                  </Typography.Text>
                </Flex>
                <Typography.Paragraph>
                  {t(
                    'application-settings.payments.onboarding-banner.benefits.benefit-two.description'
                  )}
                </Typography.Paragraph>
              </Flex>
              <Flex vertical gap={5}>
                <Flex gap={8}>
                  <SmileTwoTone
                    twoToneColor="#00D0BE"
                    style={{ fontSize: 16 }}
                  />
                  <Typography.Text style={{ fontSize: 16 }}>
                    {t(
                      'application-settings.payments.onboarding-banner.benefits.benefit-three.title'
                    )}
                  </Typography.Text>
                </Flex>
                <Typography.Paragraph>
                  {t(
                    'application-settings.payments.onboarding-banner.benefits.benefit-three.description'
                  )}
                </Typography.Paragraph>
              </Flex>
              <Flex vertical gap={5} align="flex-end" style={{ width: '100%' }}>
                {isTrialMerchant && (
                  <Button
                    type="primary"
                    icon={<RocketOutlined />}
                    onClick={handleOnUpgradeContractClick}
                  >
                    {t(
                      'application-settings.payments.onboarding-banner.buttons.upgrade-plan'
                    )}
                  </Button>
                )}
                {!isTrialMerchant && (
                  <Button
                    type="primary"
                    icon={<RocketOutlined />}
                    loading={isLoadingInitiateShorePayOnlineAccountSetup}
                    onClick={handleOnShorePayOnlineAccountButtonClick}
                  >
                    {t(
                      'application-settings.payments.onboarding-banner.buttons.start-now'
                    )}
                  </Button>
                )}
              </Flex>
            </Flex>
          </Flex>
        }
      />
      {isCaptureTaxIDModalOpen && (
        <PaymentCaptureTaxIDModal
          isLoadingShorePayOnlineSetup={
            isLoadingInitiateShorePayOnlineAccountSetup
          }
          onClickClose={handleOnCaptureTaxIDModalClose}
          onFormSubmit={handleOnCaptureTaxIDModalFormSubmit}
        />
      )}
    </>
  );
}
